import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { postsApi } from '@/lib/api'
import { toast } from '@/components/ui/use-toast'

interface DeletePostDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  postId: string
  onDelete?: (postId: string) => void
}

export function DeletePostDialog({
  open,
  onOpenChange,
  postId,
  onDelete,
}: DeletePostDialogProps) {
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsApi.deletePost(id),
    onSuccess: () => {
      // Invalidate and refetch feed queries
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['posts'] })

      toast({
        title: 'Post deleted',
        description: 'Your post has been deleted successfully',
      })

      // Call parent callback if provided
      if (onDelete) {
        onDelete(postId)
      }

      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete post',
        description: error.message || 'An error occurred while deleting the post',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      setIsDeleting(false)
    },
  })

  const handleDelete = () => {
    setIsDeleting(true)
    deleteMutation.mutate(postId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete post</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
